import { useState } from 'react';
import LangLink from '../../components/common/LangLink';
import { KeyRound, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { userService } from '../../services/api';
import SEOHead from '../../components/common/SEOHead';
import AuthLayout, { AUTH_INPUT_CLASS, AUTH_BUTTON_CLASS } from './AuthLayout';

export default function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle'); // idle | loading | success | error
    const [message, setMessage] = useState('');
    const { language } = useLanguage();
    const fr = language === 'fr';

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;

        setStatus('loading');
        setMessage('');

        try {
            const result = await userService.forgotPassword(email.trim(), language);
            setStatus('success');
            setMessage(result.message || (fr
                ? 'Si cette adresse est associée à un compte, un lien de réinitialisation a été envoyé.'
                : 'If this address is associated with an account, a reset link has been sent.'));
        } catch {
            setStatus('error');
            setMessage(fr ? 'Erreur réseau. Veuillez réessayer.' : 'Network error. Please try again.');
        }
    };

    return (
        <AuthLayout
            title={fr ? 'Mot de passe oublié ?' : 'Forgot password?'}
            subtitle={status === 'success' ? null : (fr
                ? 'Entrez votre adresse courriel pour recevoir un lien de réinitialisation.'
                : 'Enter your email address to receive a reset link.')}
            footer={
                <LangLink to="/login" className="inline-flex items-center gap-1.5 text-sm text-[#0061FE] dark:text-[#71a7ff] hover:text-[#0052db] dark:hover:text-white font-medium transition">
                    <ArrowLeft size={14} />
                    {fr ? 'Retour à la connexion' : 'Back to login'}
                </LangLink>
            }
        >
            <SEOHead title={fr ? 'Mot de passe oublié' : 'Forgot password'} canonical="/forgot-password" noindex />

            {status === 'success' ? (
                <div className="text-center">
                    <div className="w-14 h-14 bg-green-50 dark:bg-green-500/15 border border-green-200 dark:border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle size={26} className="text-green-600 dark:text-green-400" />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-[#a9b1bd] mb-6">{message}</p>
                    <LangLink to="/login" className={AUTH_BUTTON_CLASS}>
                        {fr ? 'Retour à la connexion' : 'Back to login'}
                    </LangLink>
                </div>
            ) : (
                <>
                    {/* Top icon (replaces the old centered KeyRound) */}
                    <div className="w-14 h-14 bg-[#0061FE]/10 dark:bg-[#0061FE]/15 border border-[#0061FE]/20 dark:border-[#0061FE]/30 rounded-full flex items-center justify-center mx-auto mb-6">
                        <KeyRound size={24} className="text-[#0061FE] dark:text-[#71a7ff]" />
                    </div>

                    {status === 'error' && message && (
                        <div className="mb-5 p-3.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl">
                            <p className="text-sm text-red-700 dark:text-red-300">{message}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label htmlFor="forgot-email" className="block text-xs font-semibold text-gray-700 dark:text-white/70 mb-2 uppercase tracking-wider">
                                {fr ? 'Adresse courriel' : 'Email address'}
                            </label>
                            <input
                                id="forgot-email" type="email" value={email}
                                onChange={(e) => setEmail(e.target.value)} required
                                className={AUTH_INPUT_CLASS}
                                placeholder={fr ? 'vous@exemple.com' : 'you@example.com'}
                                autoComplete="email"
                            />
                        </div>

                        <button type="submit" disabled={status === 'loading'} className={`${AUTH_BUTTON_CLASS} gap-2`}>
                            {status === 'loading' ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    {fr ? 'Envoi en cours...' : 'Sending...'}
                                </>
                            ) : (
                                fr ? 'Envoyer le lien' : 'Send reset link'
                            )}
                        </button>
                    </form>
                </>
            )}
        </AuthLayout>
    );
}
